import { projectResolutionAt } from '@morfi/contract-sdk/conviction';
import type { MarketConfig } from '../core/internal/market-config-catalog.js';
import type { MarketPipelineStage } from './market-pipeline-stage.js';
import {
  deriveMarketResolutionThresholdPolicy,
  toMarketResolutionThresholdContractPolicy,
} from './resolution-threshold.js';

export interface ResolutionThresholdProjectionOutcome {
  readonly outcomeIndex: number;
  readonly convictionAnchor: string;
  readonly convictionTarget: string;
}

export interface ProjectMarketResolutionAtInput {
  readonly pipelineStage?: MarketPipelineStage;
  readonly config: MarketConfig | undefined;
  readonly outcomeCount: number;
  readonly tradingStartedAt: string | null;
  readonly convictionUpdatedAt: string | undefined;
  readonly retentionPerSecondPpb: string | undefined;
  readonly outcomes: readonly ResolutionThresholdProjectionOutcome[] | undefined;
}

export interface ProjectMarketResolutionAtResult {
  readonly outcomeIndex: number;
  readonly resolutionAtSecs: bigint;
  /** Wall-clock milliseconds at which the winner's conviction reaches the threshold. */
  readonly crossAtMs: number;
}

export interface ResolutionCountdownParts {
  readonly days: number;
  readonly hours: number;
  readonly minutes: number;
  readonly seconds: number;
}

export type FormattedResolutionCountdown =
  | { readonly kind: 'days'; readonly days: number; readonly hours: number }
  | { readonly kind: 'clock'; readonly text: string };

/**
 * Which outcome is projected to win, and when.
 *
 * Between trades every outcome's conviction relaxes deterministically from
 * its trade-time anchor toward its target, so the moment it reaches the
 * threshold is fixed the instant the trade lands: `convictionUpdatedAt` plus
 * the closed-form time-to-resolve. Only anchors, targets and the trade
 * timestamp feed this — never the 100 ms projected conviction — so the
 * result changes only when a trade (or the config) does, and consumers can
 * count down to `crossAtMs` without re-deriving anything per tick.
 *
 * The winner is the outcome that crosses first, which is not always the one
 * with the highest conviction today: a lower outcome whose target sits above
 * the line beats a leader whose target never reaches it. Ties go to the
 * lowest outcome index. Returns `null` when nothing is projected to cross,
 * the inputs are not available yet, or `pipelineStage` is not `'open'`.
 * Already-across / resolving is that `open` gate, not the first-touch solver.
 */
export function projectMarketResolutionAt(
  input: ProjectMarketResolutionAtInput,
): ProjectMarketResolutionAtResult | null {
  if (input.pipelineStage !== undefined && input.pipelineStage !== 'open') return null;
  if (input.outcomes === undefined) return null;
  if (input.convictionUpdatedAt === undefined || input.retentionPerSecondPpb === undefined) {
    return null;
  }
  const policy = deriveMarketResolutionThresholdPolicy({
    config: input.config,
    outcomeCount: input.outcomeCount,
  });
  const contractPolicy = toMarketResolutionThresholdContractPolicy(policy, input.tradingStartedAt);
  if (contractPolicy.kind === 'unavailable') return null;

  const orderedOutcomes = orderedProjectionOutcomes(input.outcomes, input.outcomeCount);
  if (orderedOutcomes === null) return null;
  const retentionPpb = integer(input.retentionPerSecondPpb);
  const updateTsSecs = integer(input.convictionUpdatedAt);
  if (retentionPpb === null || updateTsSecs === null) return null;

  try {
    const result = projectResolutionAt(
      {
        convictionPpb: orderedOutcomes.map((outcome) => outcome.convictionAnchor),
        targetPpb: orderedOutcomes.map((outcome) => outcome.convictionTarget),
        retentionPpb,
        updateTsSecs,
      },
      contractPolicy.policy,
    );
    if (result === null) return null;
    return {
      outcomeIndex: result.outcomeIndex,
      resolutionAtSecs: result.resolutionAtSecs,
      crossAtMs: Number(result.resolutionAtSecs) * 1000,
    };
  } catch {
    return null;
  }
}

export function remainingResolutionSeconds(crossAtMs: number, nowMs: number): number {
  if (!Number.isFinite(crossAtMs) || !Number.isFinite(nowMs)) return 0;
  return Math.max(0, Math.round((crossAtMs - nowMs) / 1000));
}

export function resolutionCountdownParts(totalSeconds: number): ResolutionCountdownParts {
  const total: number = Number.isFinite(totalSeconds) ? Math.max(0, Math.trunc(totalSeconds)) : 0;
  return {
    days: Math.floor(total / 86_400),
    hours: Math.floor((total % 86_400) / 3_600),
    minutes: Math.floor((total % 3_600) / 60),
    seconds: total % 60,
  };
}

export function formatResolutionCountdown(totalSeconds: number): FormattedResolutionCountdown {
  const parts = resolutionCountdownParts(totalSeconds);
  if (parts.days > 0) return { kind: 'days', days: parts.days, hours: parts.hours };
  const seconds = String(parts.seconds).padStart(2, '0');
  if (parts.hours > 0) {
    return {
      kind: 'clock',
      text: `${String(parts.hours)}:${String(parts.minutes).padStart(2, '0')}:${seconds}`,
    };
  }
  return { kind: 'clock', text: `${String(parts.minutes)}:${seconds}` };
}

function orderedProjectionOutcomes(
  outcomes: readonly ResolutionThresholdProjectionOutcome[],
  outcomeCount: number,
): Array<{ readonly convictionAnchor: bigint; readonly convictionTarget: bigint }> | null {
  if (!Number.isSafeInteger(outcomeCount) || outcomes.length !== outcomeCount) return null;
  const sorted = [...outcomes].sort((left, right) => left.outcomeIndex - right.outcomeIndex);
  const result: Array<{ readonly convictionAnchor: bigint; readonly convictionTarget: bigint }> =
    [];
  for (let index = 0; index < sorted.length; index += 1) {
    const outcome = sorted[index];
    if (outcome === undefined || outcome.outcomeIndex !== index) return null;
    const convictionAnchor = integer(outcome.convictionAnchor);
    const convictionTarget = integer(outcome.convictionTarget);
    if (convictionAnchor === null || convictionTarget === null) return null;
    result.push({ convictionAnchor, convictionTarget });
  }
  return result;
}

function integer(value: string): bigint | null {
  return /^(0|[1-9]\d*)$/.test(value) ? BigInt(value) : null;
}
